/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * N-5 (S697): does this build hold the Stripe product identifiers of the
 * Stripe account it is pointed at?
 *
 * config/product-ids.ts reads every identifier from a build-time variable with
 * the PRODUCTION value as its fallback. So a build pointed at a Stripe test
 * account, made without those variables set, holds production identifiers while
 * its invoices carry test ones -- and nothing compares the two.
 *
 * What happens then is the defect, and it is that nothing happens. Every lookup
 * keyed on a product identifier misses at once, and each miss degrades quietly:
 * isDocumentTranslator() stops recognising the customer's own orders, so the
 * Document Translator details page has nothing to show; getProductDistribution()
 * returns nothing, so the download control offers a generic message instead of
 * the product page; the price-to-plan lookups fall through to their defaults.
 * None of it raises an error. Silence is indistinguishable from "this customer
 * owns nothing", which is why it reaches support as a code defect.
 *
 * This module compares the two lists at start-up and reports a mismatch to
 * Sentry ONCE per page load. It is deliberately NOT fatal: a shop that refuses
 * to start is worse than one that shows a generic download link. The point is to
 * make the condition a line in monitoring rather than a support case.
 *
 * @module config/product-id-verification
 */

import * as Sentry from "@sentry/react";
import { api } from "@/lib/http-client";
import { logger } from "@/lib/logger";
import {
  AUTO_FTP_V8_PRODUCT_ID,
  DICTATION_PRO_V2_PRODUCT_ID,
  DOCUMENT_TRANSLATOR_PRODUCT_ID,
  LEGACY_AUTO_FTP_ANNUAL_PRODUCT_ID,
  LEGACY_DICTATION_PRO_ANNUAL_PRODUCT_ID,
  LEGACY_MSR_ANNUAL_PRODUCT_ID,
  LEGACY_MSR_PRO_ANNUAL_PRODUCT_ID,
  MSR_PRO_PRODUCT_ID,
  MSR_V6_PRODUCT_ID,
  SECURITY_MONITOR_PRO_PRODUCT_ID,
  TSL_100K_PRODUCT_ID,
  TSL_45K_PRODUCT_ID,
  TSL_EXTRA_WORDS_CONFIGURED,
  TSL_EXTRA_WORDS_PRODUCT_ID,
  TSL_TRIAL_PRODUCT_ID,
  WEBCAM_MONITOR_PRODUCT_ID,
} from "@/config/product-ids";

/** The Stripe product identifiers this build holds, by constant name. */
export function buildStripeProductIds(): Record<string, string> {
  const ids: Record<string, string> = {
    DOCUMENT_TRANSLATOR_PRODUCT_ID,
    TSL_TRIAL_PRODUCT_ID,
    TSL_45K_PRODUCT_ID,
    TSL_100K_PRODUCT_ID,
    LEGACY_AUTO_FTP_ANNUAL_PRODUCT_ID,
    LEGACY_MSR_ANNUAL_PRODUCT_ID,
    LEGACY_DICTATION_PRO_ANNUAL_PRODUCT_ID,
    LEGACY_MSR_PRO_ANNUAL_PRODUCT_ID,
    MSR_V6_PRODUCT_ID,
    DICTATION_PRO_V2_PRODUCT_ID,
    MSR_PRO_PRODUCT_ID,
    AUTO_FTP_V8_PRODUCT_ID,
    SECURITY_MONITOR_PRO_PRODUCT_ID,
    WEBCAM_MONITOR_PRODUCT_ID,
  };
  // NF-1 / D-1 (S576): the Extra Words identifier is a placeholder until
  // somebody sets the variable, and the cart does not offer the product while it
  // is. Comparing a placeholder would report a mismatch that is already known
  // and already handled, on every page load, in every environment.
  if (TSL_EXTRA_WORDS_CONFIGURED) {
    ids.TSL_EXTRA_WORDS_PRODUCT_ID = TSL_EXTRA_WORDS_PRODUCT_ID;
  }
  return ids;
}

/** The shape this check needs out of GET /api/v1/config/product-ids/. */
interface ProductIdConfigShape {
  version?: number;
  qlm_to_stripe?: Record<string, string>;
  stripe_to_qlm?: Record<string, string | number>;
  tsl_features_to_stripe?: Record<string, string>;
  tsl_paid_products?: string[];
}

/**
 * Every Stripe product identifier the back end serves, from all four fields
 * that carry one.
 *
 * All four, not one: the back end states the same identifiers in several
 * places, and a check that read only the first would report a false mismatch
 * for any product named only in the others.
 */
export function backendStripeProductIds(payload: ProductIdConfigShape | null | undefined): Set<string> {
  const out = new Set<string>();
  const add = (value: unknown) => {
    if (typeof value === "string" && value.startsWith("prod_")) out.add(value);
  };
  if (!payload || typeof payload !== "object") return out;
  Object.values(payload.qlm_to_stripe ?? {}).forEach(add);
  Object.keys(payload.stripe_to_qlm ?? {}).forEach(add);
  Object.values(payload.tsl_features_to_stripe ?? {}).forEach(add);
  (payload.tsl_paid_products ?? []).forEach(add);
  return out;
}

export interface ProductIdMismatch {
  /** The constant that holds the identifier the back end does not know. */
  constantName: string;
  /** The identifier itself. */
  productId: string;
}

/**
 * The build identifiers the back end does not serve.
 *
 * An EMPTY backend set is not a mismatch. It means the round trip failed, the
 * payload was malformed, or the deployment serves no map -- none of which is
 * evidence about the build's identifiers, and reporting it as one would train
 * everybody to ignore this alert.
 */
export function findProductIdMismatches(
  build: Record<string, string>,
  backend: Set<string>,
): ProductIdMismatch[] {
  if (backend.size === 0) return [];
  return Object.entries(build)
    .filter(([, productId]) => productId && !backend.has(productId))
    .map(([constantName, productId]) => ({ constantName, productId }));
}

/** Once per page load, exactly as the item asks. */
let reported = false;

/** Test seam: reset the once-per-page-load latch. */
export function resetProductIdVerificationForTests(): void {
  reported = false;
}

/**
 * Compare the build's identifiers against the back end's and report a mismatch.
 *
 * Never throws and never rejects: this runs at start-up and a configuration
 * check that can stop the shop starting is a worse defect than the one it looks
 * for. Returns the mismatches it found so a caller (and the test suite) can see
 * the answer without reading Sentry.
 */
export async function verifyProductIdsAgainstBackend(): Promise<ProductIdMismatch[]> {
  if (reported) return [];
  try {
    const response = await api.get<ProductIdConfigShape>("/config/product-ids/");
    const backend = backendStripeProductIds(response?.data);
    const build = buildStripeProductIds();
    const mismatches = findProductIdMismatches(build, backend);
    if (mismatches.length === 0) return [];

    reported = true;
    const buildList = Object.values(build).sort();
    const backendList = [...backend].sort();

    // Both lists in full, in the log, because the identifier alone does not say
    // which account it belongs to -- seeing the two side by side is what makes
    // "this build is pointed at the wrong Stripe account" readable at a glance.
    logger.error(
      "Stripe product identifiers do not match the back end. " +
        "This build was probably made without the VITE_STRIPE_PRODUCT_ID_* variables " +
        "for the Stripe account it is pointed at; every product lookup will miss silently.",
      {
        buildProductIds: buildList,
        backendProductIds: backendList,
        unknownToBackend: mismatches,
      },
    );
    Sentry.captureMessage("config: Stripe product identifiers do not match the back end", {
      level: "error",
      tags: { area: "config.product-ids" },
      extra: {
        unknown_to_backend: mismatches.map((m) => `${m.constantName}=${m.productId}`),
        build_product_ids: buildList,
        backend_product_ids: backendList,
      },
    });
    return mismatches;
  } catch (error) {
    // Not fatal, and not reported as a mismatch either: a failed round trip is
    // the product-id config service's own concern and it already logs it.
    logger.warn("Product-id verification: could not read the backend map", error);
    return [];
  }
}
